'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { ROUTES } from '@repo/shared/constants';
import BrandLogo from '@/components/icons/BrandLogo';

export function LoginForm() {
  // Prefill email when the user arrives from an invitation link
  // (`/login?email=…`). Loose sanity-check keeps pathological payloads
  // out of the input — invalid values just fall back to empty.
  const searchParams = useSearchParams();
  const prefilledEmail = searchParams?.get('email')?.trim().toLowerCase() ?? '';
  const initialEmail =
    prefilledEmail.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(prefilledEmail)
      ? prefilledEmail
      : '';
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [agencyLogoUrl, setAgencyLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/public/agency-logo')
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        if (!cancelled) setAgencyLogoUrl(json?.logo_url ?? null);
      })
      .catch(() => { /* fall back to brand wordmark */ });
    return () => { cancelled = true; };
  }, []);

  const handleSendCode = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setIsCodeSent(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      // verifyOtp on the server already set the Supabase session cookies via
      // the @supabase/ssr cookie bridge — safe to redirect immediately.
      window.location.href = ROUTES.landing;
    } catch (err: any) {
      setError(err.message);
      // Reset on auth failure
      setEmail('');
      setCode('');
      setIsCodeSent(false);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isCodeSent) {
      handleVerifyCode();
    } else {
      handleSendCode();
    }
  };

  const onCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
  };

  return (
    <div
      className="w-full flex items-center justify-center min-h-svh px-4 sm:px-0"
      style={{ background: '#FAFAFB' }}
    >
      <div
        className="w-full flex flex-col sm:flex-row overflow-hidden"
        style={{
          maxWidth: '900px',
          minHeight: '70vh',
          background: '#FFFFFF',
          border: '1px solid #EEEFF1',
          borderRadius: '12px',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
        }}
      >
        {/* Left Side — Form */}
        <div className="w-full sm:w-1/2 p-6 md:p-10 flex flex-col justify-between gap-6">
          <div>
            <h1 style={{ color: '#232529', fontSize: '22px', fontWeight: 700 }}>
              Welcome Back
            </h1>
            <p style={{ color: '#5B5E63', fontSize: '14px', marginTop: '4px' }}>
              Sign in with your email to get a one-time code.
            </p>
          </div>

          {error && (
            <div
              className="p-3 rounded-lg text-sm"
              style={{ background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA' }}
            >
              {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 mt-2">
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  style={{ color: '#232529', fontSize: '14px', fontWeight: 500 }}
                  className="block mb-1.5"
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  inputMode="email"
                  required
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  readOnly={isCodeSent}
                  className="w-full px-3 outline-none"
                  style={{
                    height: '44px',
                    background: '#FFFFFF',
                    border: '1px solid #D9D9D9',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#232529',
                    opacity: isCodeSent ? 0.6 : 1,
                    cursor: isCodeSent ? 'not-allowed' : 'text',
                  }}
                />
              </div>

              {/* Code */}
              {isCodeSent && (
                <div>
                  <label
                    htmlFor="code"
                    style={{ color: '#232529', fontSize: '14px', fontWeight: 500 }}
                    className="block mb-1.5"
                  >
                    6-Digit Authentication Code
                  </label>
                  <input
                    id="code"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{6}"
                    placeholder="------"
                    value={code}
                    onChange={onCodeChange}
                    autoFocus
                    className="w-full px-3 outline-none"
                    style={{
                      height: '44px',
                      background: '#FFFFFF',
                      border: '1px solid #D9D9D9',
                      borderRadius: '8px',
                      fontSize: '14px',
                      color: '#232529',
                    }}
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center"
                style={{
                  height: '44px',
                  fontSize: '15px',
                  fontWeight: 600,
                  borderRadius: '9px',
                  background: loading ? '#5B9BF5' : '#276CF0',
                  color: 'white',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'background 150ms',
                }}
                onMouseEnter={(e) => { if (!loading) (e.target as HTMLElement).style.background = '#0C55E2'; }}
                onMouseLeave={(e) => { if (!loading) (e.target as HTMLElement).style.background = '#276CF0'; }}
              >
                {loading ? (
                  <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  isCodeSent ? 'Confirm' : 'Email Code'
                )}
              </button>

              {isCodeSent && (
                <div className="flex items-center justify-center gap-2">
                  <button
                    type="button"
                    disabled={loading || !email}
                    onClick={() => handleSendCode()}
                    style={{ fontSize: '13px', color: '#6B6F76', background: 'none', border: 'none', cursor: 'pointer' }}
                    className="hover:underline"
                  >
                    Resend code
                  </button>
                  <span style={{ color: '#D9D9D9', fontSize: '13px' }}>•</span>
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => { setIsCodeSent(false); setEmail(''); setCode(''); setError(''); }}
                    style={{ fontSize: '13px', color: '#6B6F76', background: 'none', border: 'none', cursor: 'pointer' }}
                    className="hover:underline"
                  >
                    Return to login
                  </button>
                </div>
              )}
            </div>
          </form>

          <p style={{ color: '#9A9DA3', fontSize: '12px' }} className="text-center sm:text-left">
            By continuing, you agree to {process.env.NEXT_PUBLIC_WORKSPACE_NAME ?? 'the workspace'}&apos;s Terms and Privacy Policy.
          </p>
        </div>

        {/* Right Side — Logo (shows first on mobile via order) */}
        <div
          className="w-full sm:w-1/2 flex items-center justify-center py-10 sm:py-0 order-first sm:order-last"
          style={{ background: '#F5F5F5' }}
        >
          {agencyLogoUrl ? (
            <div
              className="flex items-center justify-center"
              style={{
                width: '96px',
                height: '96px',
                borderRadius: '24px',
                background: '#FFFFFF',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                overflow: 'hidden',
              }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={agencyLogoUrl}
                alt="Workspace logo"
                style={{ maxWidth: '76px', maxHeight: '76px', objectFit: 'contain' }}
              />
            </div>
          ) : (
            <BrandLogo size={96} />
          )}
        </div>
      </div>
    </div>
  );
}
